// Vite bundles the worklet and its imports as a separate audio-thread module.
// eslint-disable-next-line boundaries/no-ignored
import processorUrl from './pcmStreamProcessor.ts?worker&url'
import type { PcmStreamInput, PcmStreamStatus } from './pcmStreamBuffer.ts'
import { waitForRendering } from './rendererHealth.ts'

const moduleLoads = new WeakMap<BaseAudioContext, Promise<void>>()

/** Streams all PCM through one processor instead of resampling each packet. */
export class PcmStreamPlayer {
  private readonly node: AudioWorkletNode
  private total = 0
  private pendingByte?: number
  private inputEnded = false
  private stopped = false
  private started = false
  heartbeatCount = 0
  private resolve!: () => void
  private reject!: (error: Error) => void
  readonly finished = new Promise<void>((resolve, reject) => {
    this.resolve = resolve
    this.reject = reject
  })

  static async prepare(context: BaseAudioContext, signal: AbortSignal) {
    // The DOM types assume secure contexts with AudioWorklet support.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!context.audioWorklet) throw new Error('Streaming audio is unavailable')
    let loading = moduleLoads.get(context)
    if (!loading) {
      loading = context.audioWorklet
        .addModule(processorUrl)
        .catch((error: unknown) => {
          moduleLoads.delete(context)
          throw error
        })
      moduleLoads.set(context, loading)
    }
    const moduleReady = loading
    await new Promise<void>((resolve, reject) => {
      const finish = (error?: unknown) => {
        clearTimeout(timeout)
        signal.removeEventListener('abort', cancel)
        if (error) reject(error)
        else resolve()
      }
      const cancel = () => {
        finish(new DOMException('Playback cancelled', 'AbortError'))
      }
      const timeout = setTimeout(() => {
        finish(new Error('Streaming audio failed to load'))
      }, 10000)
      // Attach the rejection handler even if this particular caller cancels.
      void moduleReady.then(() => {
        finish()
      }, finish)
      signal.addEventListener('abort', cancel, { once: true })
      if (signal.aborted) cancel()
    })
  }

  constructor(
    private context: AudioContext,
    private progress: (value: number) => void,
    private onStart?: () => void,
  ) {
    this.node = new AudioWorkletNode(context, 'teacher-pcm-stream', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    })
    this.node.port.onmessage = ({ data }: MessageEvent<PcmStreamStatus>) => {
      if (this.stopped) return
      // Heartbeats let the renderer detect and recover a stalled audio thread.
      this.heartbeatCount++
      if (data.started && !this.started) {
        this.started = true
        this.onStart?.()
      }
      this.progress(
        this.inputEnded ? Math.min(data.played / this.total, 0.999) : 0,
      )
      if (data.done) {
        this.progress(1)
        this.stop()
      }
    }
    this.node.onprocessorerror = (event) => {
      const native = event as Partial<ErrorEvent>
      const error = Object.assign(
        new Error('Streaming audio playback failed'),
        {
          cause: {
            type: event.type,
            message: native.message,
            filename: native.filename,
            lineno: native.lineno,
            colno: native.colno,
            error: native.error as unknown,
          },
        },
      )
      this.reject(error)
      this.stop()
    }
    this.node.connect(context.destination)
  }

  waitForRendering(signal: AbortSignal, paused: () => boolean) {
    return waitForRendering(
      () => ({
        clockTime: this.context.currentTime,
        heartbeats: this.heartbeatCount,
      }),
      signal,
      paused,
      this.finished,
    )
  }

  private send(message: PcmStreamInput, transfer: Transferable[] = []) {
    this.node.port.postMessage(message, transfer)
  }

  append(chunk: Uint8Array) {
    if (this.stopped) return
    let bytes = chunk
    if (this.pendingByte !== undefined) {
      bytes = new Uint8Array(chunk.length + 1)
      bytes[0] = this.pendingByte
      bytes.set(chunk, 1)
    }
    this.pendingByte = bytes.length % 2 ? bytes[bytes.length - 1] : undefined
    const length = Math.floor(bytes.length / 2)
    if (!length) return
    const samples = new Float32Array(length)
    const view = new DataView(bytes.buffer, bytes.byteOffset, length * 2)
    for (let i = 0; i < length; i++) {
      const value = view.getInt16(i * 2, true) / 32768
      samples[i] = value
    }
    this.total += length
    this.send({ type: 'audio', samples }, [samples.buffer])
  }

  finishInput() {
    if (!this.total || this.pendingByte !== undefined)
      throw new Error('Invalid PCM audio')
    this.inputEnded = true
    this.send({ type: 'end' })
  }

  stop() {
    if (this.stopped) return
    this.stopped = true
    this.send({ type: 'stop' })
    this.node.port.onmessage = null
    this.node.onprocessorerror = null
    this.node.disconnect()
    this.node.port.close()
    this.resolve()
  }
}
